import axios from "axios";

import {
  ACCOUNT_BASE_URL,
  API_BASE_URL,
  PRODUCT_BASE_URL,
  CHECKOUT_BASE_URL,
  ORDER_BASE_URL,
} from "@/constants/app-defaults";

// Helper to read the correct auth token for the current platform.
// Merchant platform (/merchant/*) uses an HttpOnly-style cookie set by the
// server-side /api/login route. User platform uses localStorage set during
// OTP verification. This separation prevents session cross-contamination.
const getAuthToken = (): string | undefined => {
  if (typeof window === "undefined") return undefined;

  const pathname = window.location.pathname;

  if (pathname.startsWith("/merchant")) {
    // Merchant platform — token lives in localStorage
    try {
      return localStorage.getItem("merchant_access_token") ?? undefined;
    } catch {
      return undefined;
    }
  }

  // User platform — token lives in localStorage
  try {
    return localStorage.getItem("accessToken") ?? undefined;
  } catch {
    return undefined;
  }
};

let isRefreshing = false;
let refreshSubscribers: ((token: string | undefined) => void)[] = [];

function onRefreshed(token: string | undefined) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string | undefined) => void) {
  refreshSubscribers.push(cb);
}

// Factory to create a custom axios instance with interceptors
const createAxiosInstance = (baseURL: string) => {
  const instance = axios.create({
    baseURL,
    timeout: 20000, // Increased from 10000 to 20000 for slow backend calls
  });

  // Attach request interceptor to always use latest auth token
  instance.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set x-platform header
    if (typeof window !== "undefined") {
      const pathname = window.location.pathname;
      if (!pathname.startsWith("/merchant")) {
        config.headers["x-platform-code"] = "TP";
      } else {
        config.headers["x-platform-code"] = "TM";
      }
    } else {
      // Default to TP on server as most SSR pages are for the user platform
      config.headers["x-platform-code"] = "TP";
    }
    return config;
  });

  // Attach response interceptor for 401 token refreshing
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Only handle 401s, prevent infinite loops, and ensure we're on client side
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        typeof window !== "undefined"
      ) {
        originalRequest._retry = true;

        const pathname = window.location.pathname;

        // Currently we only implemented auto-refresh for the merchant platform
        if (pathname.startsWith("/merchant")) {
          if (isRefreshing) {
            return new Promise((resolve) => {
              addRefreshSubscriber((token) => {
                if (token) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(axios(originalRequest));
              });
            });
          }

          isRefreshing = true;

          try {
            // Import dynamically to avoid circular dependencies if any
            const { refreshToken: doRefreshToken } = await import("@/features/merchant-dashboard/lib/auth-service");
            await doRefreshToken();

            const newToken = getAuthToken();
            onRefreshed(newToken);

            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return axios(originalRequest);
          } catch (refreshError) {
            onRefreshed(undefined);

            // If refresh fails, session is expired
            if (!sessionStorage.getItem("auth_redirecting")) {
              sessionStorage.setItem("auth_redirecting", "true");
              sessionStorage.setItem("redirect_after_login", pathname + window.location.search);

              // Clear tokens client-side as a fallback
              localStorage.removeItem("merchant_access_token");
              localStorage.removeItem("merchant_refresh_token_v2");

              // Dispatch event instead of hard reloading, letting UI handle it if possible
              window.dispatchEvent(new Event("merchant-auth-expired"));

              // Still do a fallback redirect after a short delay in case no listener exists
              setTimeout(() => {
                window.location.href = "/merchant";
              }, 500);
            }
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

// Main instance
export const axiosInstance = createAxiosInstance(API_BASE_URL);

// Product-specific instance
export const productAxiosInstance = createAxiosInstance(
  `${API_BASE_URL}${PRODUCT_BASE_URL}`,
);

// Account-specific instance
export const accountAxiosInstance = createAxiosInstance(
  `${API_BASE_URL}${ACCOUNT_BASE_URL}`,
);

// Checkout instance

export const checkoutAxiosInstance = createAxiosInstance(
  `${API_BASE_URL}${CHECKOUT_BASE_URL}`,
);

export const orderAxiosInstance = createAxiosInstance(
  `${API_BASE_URL}${ORDER_BASE_URL}`,
);

export default axiosInstance;
